// ActiveFiltersLine.jsx

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

// Path to your globalReportFilters action
import { setJS } from "../../../actions/globalReportFilters";

// Re‑use the generic chip renderer that already exists
import { FilterChips } from "../../FiltersDropdown";

/**
 * Default set of filters so the previous behaviour in <FiltersByType /> works
 * out‑of‑the‑box.  If you render <ActiveFiltersLine /> without passing the
 * `filters` prop you will get exactly the same chips as before.
 */
export const DEFAULT_FILTERS = {
    Countries: { label: "Country" },
    Offers: { label: "Rate plan" },
    Rooms: { label: "Room" },
    Types: { label: "Type" },
    Extras: { label: "Extras" },
    VoucherNames: { label: "Voucher" },
};

/**
 * Unified/chip‑only representation for the filters currently held in
 * `globalReportFilters` (or in any slice you pass through `stateSelector`).
 *
 * The component is controlled _internally_ by default (same behaviour as the
 * original `ActiveFiltersLine`).  If you need full control from the parent –
 * e.g. in `CommissionFilters`, where you keep additional local copies such as
 * `selectedHotels` – you can switch it to **controlled** mode by providing the
 * `activeFilters` + `onActiveFiltersChange` props.
 *
 *    <ActiveFiltersLine
 *        filters={{ Hotels: { label: 'Hotel' }, Currencies: { label: 'Currency'} }}
 *        activeFilters={{ Hotels: selectedHotels, Currencies: selectedCurrencies }}
 *        onActiveFiltersChange={({ Hotels, Currencies }) => {
 *            setSelectedHotels(Hotels);
 *            setSelectedCurrencies(Currencies);
 *        }}
 *    />
 */
const ActiveFiltersLine = ({
    /**
     * Map<FilterKey, { label: string, options?: Record<string, string> }>  –  the
     * same structure that `<FilterDropdown />` expects.  `options` are used only
     * for the tooltip chips, so you can omit them when not needed.
     */
    filters = DEFAULT_FILTERS,

    /**
     * Allows you to read the JS slice from a different point in the store.  Keep
     * the default for backwards‑compat.
     */
    stateSelector = (state) => state.globalReportFilters,

    /**
     * Controlled‑mode props.  If *both* are provided the component will no longer
     * touch Redux; instead the parent component becomes the single source of
     * truth.  Very handy for `CommissionFilters` where we already hold the lists
     * in local state.
     */
    activeFilters: activeFiltersProp,
    onActiveFiltersChange,

    /**
     * Any extra props are forwarded directly to <FilterChips /> (e.g. sx)
     */
    ...rest
}) => {
    const js = useSelector(stateSelector);
    const dispatch = useDispatch();

    // INTERNAL‑state (uncontrolled) ------------------------------------------------
    const buildFromJS = () =>
        Object.fromEntries(
            Object.keys(filters).map((k) => [k, (js.filters ?? {})[k] ?? []])
        );

    const [internalFilters, setInternalFilters] = useState(buildFromJS());

    // Keep internal state in sync with the store when we are uncontrolled.
    useEffect(() => {
        if (activeFiltersProp !== undefined) return; // parent drives the value
        setInternalFilters(buildFromJS());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [js]);

    // Determine which pair we should use today
    const isControlled =
        activeFiltersProp !== undefined &&
        onActiveFiltersChange instanceof Function;
    const activeFilters = isControlled ? activeFiltersProp : internalFilters;

    const setActiveFilters = (updatedFilters) => {
        if (isControlled) {
            // Parent‑controlled – delegate upwards
            onActiveFiltersChange(updatedFilters);
            return;
        }

        // Original behaviour – write through to Redux
        const newJS = {
            ...js,
            filters: { ...(js.filters ?? {}), ...updatedFilters },
        };
        dispatch(setJS(newJS));
    };

    return (
        <FilterChips
            activeFilters={activeFilters}
            setActiveFilters={setActiveFilters}
            filters={filters}
            {...rest}
        />
    );
};

export default ActiveFiltersLine;
